<script lang="ts" context="module">
  export const searchResultContext = Symbol();

  export interface SearchResultContext {
    searchResult: Writable<SearchResult>;
  }
</script>

<script lang="ts">
  import LL, { locale } from '$i18n/i18n-svelte';
  import { entries } from '$lib/utils/object';
  import { distance } from 'fastest-levenshtein';
  import { Search as SearchIcon, X } from 'lucide-svelte';
  import { writable, type Writable } from 'svelte/store';
  import { focusableChildren } from './focus';
  import { search, type SearchResult } from './search';
  import SearchResultCharacters from './search-result-characters.svelte';
  import SearchResultItems from './search-result-items.svelte';
  import SearchResultPlayers from './search-result-players.svelte';
  import { goto } from '$app/navigation';
  import { setContext } from 'svelte';
  import { navigating } from '$app/stores';

  export let isStandalone = false;
  export let onEnter: (() => void) | undefined = undefined;
  export let onClose: (() => void) | undefined = undefined;

  $: isResultVisible = !isStandalone;
  const searchResult = writable<SearchResult>();

  setContext(searchResultContext, {
    searchResult,
  });

  let searchResultBlocks = [
    { key: 'players', component: SearchResultPlayers },
    { key: 'items', component: SearchResultItems },
    // TODO [홈-검색]
    { key: 'characters', component: SearchResultCharacters },
    // { key: 'routes', component: SearchResultRoutes },
  ] satisfies Array<{ key: keyof SearchResult; component: any }>;

  const searchPrefix = {
    players: ['ㅍ', 'p'],
    // TODO [홈-검색]
    characters: ['ㅋ', 'c'],
    items: ['ㅇ', 'i'],
    // routes: ['ㄹ', 'r'],
  } satisfies Record<keyof SearchResult, string[]>;

  const flattenedPrefix = Object.values(searchPrefix).flat();

  const searchDebounceMs = 300;
  let rawSearchValue = '';
  $: prefixCandidate = rawSearchValue.split(' ')[0];
  $: isPrefixed = flattenedPrefix.includes(prefixCandidate);
  $: searchValueWithoutPrefix = rawSearchValue.slice(1);
  $: sanitizedSearchValue = isPrefixed ? searchValueWithoutPrefix.trim() : rawSearchValue.trim();

  searchResult.subscribe((result) => {
    searchResultBlocks = searchResultBlocks.sort(
      (a, b) =>
        getSortDistance(a.key, result?.[a.key] ?? []) -
        getSortDistance(b.key, result?.[b.key] ?? []),
    );
  });

  function getSortDistance(
    category: keyof SearchResult,
    candidate: Array<{ id: number; name?: string; title?: string }>,
  ): number {
    if (isPrefixed && searchPrefix[category].includes(prefixCandidate)) {
      return -1;
    }

    let min = 9999;
    for (const c of candidate) {
      min = Math.min(distance(sanitizedSearchValue, c.name ?? c.title ?? ''), min);
    }
    return min;
  }

  let timeoutId: NodeJS.Timeout | undefined;

  function handleSearchInput(e: Event | FocusEvent | KeyboardEvent) {
    if (e instanceof KeyboardEvent) {
      if (e.key === 'Enter' && sanitizedSearchValue) {
        e.preventDefault();
        if (!e.currentTarget) {
          return;
        }
        const group = focusableChildren(document.querySelector('.search') as HTMLElement);
        const target = group.next('a', true);

        onEnter?.();
        clearTimeout(timeoutId);
        if (
          target != undefined &&
          target.querySelector('.content')?.textContent?.trim() === sanitizedSearchValue
        ) {
          target.focus();
          goto((target as HTMLAnchorElement)?.href);
        } else {
          goto(`/${$locale}/players/${sanitizedSearchValue}`);
        }
      }
      return;
    }
    isResultVisible = true;
    clearTimeout(timeoutId);
    timeoutId = setTimeout(async () => {
      searchResult.set(await search(sanitizedSearchValue));
    }, searchDebounceMs);
  }

  function handleKeydown(e: KeyboardEvent) {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      if (!e.currentTarget) {
        return;
      }
      const group = focusableChildren(e.currentTarget as HTMLElement);
      const selector = 'a, input';

      if (e.key === 'ArrowDown') {
        group.next(selector);
      } else {
        group.prev(selector);
      }
    }
  }

  $: if ($navigating) {
    onClose?.();
  }
</script>

<section
  class="search"
  class:is-result-visible={isResultVisible}
  class:is-standalone={isStandalone}
  on:keydown={handleKeydown}
  role="searchbox"
  tabindex="0"
>
  <div class="main-search">
    <button
      class="close-button"
      on:click={() => {
        if (isStandalone) {
          isResultVisible = false;
        }
        onClose?.();
      }}
    >
      <X />
    </button>
    <input
      class="search-input"
      placeholder={$LL.search.placeholder()}
      autocomplete="off"
      on:focus={handleSearchInput}
      on:input={handleSearchInput}
      on:keydown={handleSearchInput}
      bind:value={rawSearchValue}
    />
    <button class="search-button">
      <SearchIcon />
    </button>
  </div>
  <section class="search-result">
    <section class="shortcut-help">
      <h2>{$LL.search.shortcutHint()}</h2>
      <div class="shortcuts">
        {#each entries(searchPrefix) as prefixEntry}
          {@const prefixKey = prefixEntry[0]}
          {@const prefixCandidates = prefixEntry[1]}
          <div>
            <kbd>{$locale === 'ko' ? prefixCandidates[0] : prefixCandidates[1]}</kbd>
            {$LL.search.resultGroups[prefixKey]()}
          </div>
        {/each}
      </div>
    </section>
    {#each searchResultBlocks as block}
      {#if $searchResult?.[block.key]?.length ?? 0 > 0}
        <svelte:component this={block.component} />
      {/if}
    {/each}
  </section>
</section>

<style lang="postcss">
  @import '$lib/styles/breakpoints.css';
  @custom-media --non-fullscreen (--sm);

  .search {
    ---border: 1px solid var(--border-emphasis);
    ---shadow: 0 1rem 2rem 0.5rem var(--mauve-a3);
    ---background: var(--background);

    display: flex;
    flex-direction: column;
    justify-content: stretch;
    position: relative;
    margin: 0 var(--page-margin-x);

    &.is-standalone {
      margin-top: var(--space-8);
    }

    &.is-result-visible {
      margin: 0;
      position: absolute;
      width: 100vw;
      height: 100vh;
      top: 0;
      left: 0;
      z-index: 20;
      border-bottom-left-radius: 0;
      border-bottom-right-radius: 0;
    }

    @media (--non-fullscreen) {
      position: relative;
      margin: 0 auto;
      width: 100%;
      max-width: 50rem;

      &.is-result-visible {
        margin: 0 auto;
        position: relative;
        height: fit-content;
      }

      &.is-standalone {
        margin-top: var(--space-8);
      }
    }

    :global(.search-group .raw-item-image) {
      border: none !important;
      padding: var(--space-1) !important;
      border-radius: var(--radius-2);
    }
  }
  :global(.dark) .search {
    ---shadow: 0 1rem 2rem 0.5rem var(--black-a8);
    ---background: var(--mauve-3);
  }
  .search > :global(section) {
    padding: var(--space-4);
  }
  .search.is-result-visible :global(.search-bookmark) {
    display: none;
  }
  :not(.search.is-result-visible) .search-result {
    display: none;
  }

  .main-search {
    display: flex;
    padding: var(--space-2);
    background: var(---background);
    box-shadow: var(---shadow);
    border-radius: var(--radius-3);
    border: var(---border);

    .is-result-visible & {
      border: none;
      border-bottom: var(---border);
      border-radius: 0;

      @media (--non-fullscreen) {
        border: var(---border);
        border-top-left-radius: var(--radius-3);
        border-top-right-radius: var(--radius-3);
      }
    }

    /* @media (--non-fullscreen) {
			.is-result-visible & {
				border: var(---border);
				border-radius: var(--radius-3);
			}
		} */

    .search-button {
      display: flex;
      place-items: center;
      place-content: center;
      width: 6rem;
      background-color: transparent;
      border: none;
      font-size: 2rem;
    }
  }

  .search-input {
    flex: 1 1 0;
    min-width: 0;
    width: 0;
    font-size: var(--size-8);
    border: none;
    padding: var(--space-4);
    background: inherit;
    padding-left: 1.6rem;

    &:focus {
      box-shadow: none;
    }
    &::placeholder {
      color: var(--mauve-8);
    }
  }

  .search-result {
    pointer-events: initial;
    /* width: 100%; */
    flex: 1;
    overflow-y: auto;
    background: var(---background);
    box-shadow: var(---shadow);

    @media (--non-fullscreen) {
      position: absolute;
      width: 100%;
      max-height: 50vh;
      top: 100%;
      border-bottom-left-radius: var(--radius-3);
      border-bottom-right-radius: var(--radius-3);
      border: var(---border);
      border-top: none;
    }
  }
  .search-result section {
    padding: 0 var(--space-3);
  }

  .search.is-result-visible + :global(.search-bookmark) {
    visibility: hidden;
  }

  .close-button {
    flex: 0 0 auto;
    padding: var(--size-4);
    padding-right: 0;
    background-color: transparent;
    border: none;
    display: none;

    .is-result-visible & {
      display: block;
    }
  }

  .shortcut-help {
    font-size: var(--size-5);
    display: flex;
    justify-content: space-between;
  }
  .shortcut-help h2 {
    font-size: inherit;
    color: var(--mauve-10);
  }
  .shortcuts {
    display: grid;
    gap: var(--space-3);

    grid-template-columns: repeat(2, auto);

    @media (--non-fullscreen) {
      /* TODO [홈-검색] 4로 변경 */
      grid-template-columns: repeat(2, auto);
    }
  }
</style>
